// Words for what the code-map index saw of a repository's agent instructions and spec
// documents. Presence only: "found" or "not found in the scanned locations", never a
// judgement of quality or practice (docs/superpowers/specs/2026-10-11-steering-coverage-design.md).
import type { SteeringBlock, SteeringSummary } from "@/api/types";

export const GAP_LABEL: Record<string, string> = {
  no_agent_instructions: "No agent instructions found",
  no_specs: "No spec documents found",
};

/** A short chip for the repo, or null when nothing is missing (or it wasn't checked). */
export function steeringChip(s: SteeringSummary | SteeringBlock | null | undefined): string | null {
  if (!s || s.skipped) return null;
  const gaps = s.gaps ?? [];
  if (gaps.includes("no_agent_instructions") && gaps.includes("no_specs")) return "no instructions or specs found";
  if (gaps.includes("no_agent_instructions")) return "no agent instructions found";
  if (gaps.includes("no_specs")) return "no specs found";
  return null;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** What was found, one line each, for the map's inspector. */
export function steeringFound(b: SteeringBlock): string[] {
  if (b.skipped) return ["Not checked: the workspace config skips this repository."];
  const out: string[] = [];
  // Files that cover the same roots share a line: "CLAUDE.md, .cursor/rules/a.mdc and 4 more cover src".
  const groups = new Map<string, { path: string; generated: boolean }[]>();
  for (const f of b.instructions?.files ?? []) {
    const key = f.covers.filter(Boolean).join(", ");
    groups.set(key, [...(groups.get(key) ?? []), f]);
  }
  for (const [covers, files] of groups) {
    const names = files.slice(0, 2).map((f) => `${f.path}${f.generated ? " (marked generated)" : ""}`).join(", ");
    const more = files.length > 2 ? ` and ${files.length - 2} more` : "";
    out.push(`${names}${more} ${files.length === 1 ? "covers" : "cover"} ${covers || "the whole repository"}`);
  }
  if (b.specs?.files) out.push(`${plural(b.specs.files, "spec document")} in ${b.specs.dirs.join(", ")}${b.specs.layouts.length ? ` (${b.specs.layouts.join(", ")} layout)` : ""}`);
  if (b.decisions?.files) out.push(`${plural(b.decisions.files, "decision record")} in ${b.decisions.dirs.join(", ")}`);
  if (b.instructions?.nested.length) out.push(`Below the source roots: ${b.instructions.nested.slice(0, 3).join(", ")}${b.instructions.nested_total > 3 ? ` and ${b.instructions.nested_total - 3} more` : ""}`);
  if (b.readme) out.push(b.readme);
  if (b.contributing) out.push(b.contributing);
  return out;
}

/** "acme/backend" → "backend". */
export function repoShort(repo: string): string {
  return repo.split("/").pop() ?? repo;
}
